import { Component, OnInit, AfterViewInit, OnDestroy, Input, ChangeDetectionStrategy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { StarbaseSearchComponent, IStarbaseModularSearchField, TrackByUID, TruncateString } from 'starbase-launcher';
import { ImageFile } from '../models/imageFile';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { TypeaheadResults } from '../models/typeaheadResults';
import { ImageTypes } from '../models/imageType';
import { LookupDataService } from '../services/lookup-data.service';
import { catchError, switchMap, takeUntil, finalize, tap } from 'rxjs/operators';
import { AppComponent } from '../app.component';
import { KeyValue } from '@angular/common';
import { GalleryTypeService } from '../services/gallery-type.service';
import { ModularSearchField_ext } from '../models/ModularSearchField_ext';
import { BreakpointService } from '../breakpoint.service';

class ExtendedModularSearchField<T, X> implements IStarbaseModularSearchField<T> {
  fieldName: string;
  displayName?: string;
  entityProps?: string[];
  default: boolean;
  enabled: boolean;
  filterClause: (value: T, search: string) => boolean;
  outerFilterClause: (value: X, search: string) => boolean;
}

@Component({
  selector: 'app-library-search',
  templateUrl: './library-search.component.html',
  styleUrls: ['./library-search.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LibrarySearchComponent extends StarbaseSearchComponent<TypeaheadResults> implements OnInit, AfterViewInit, OnDestroy {
  @Input() placeholderText = 'Search Library';
  @Input() data: TypeaheadResults[];
  /** Set to 0 if in *All* library mode. */
  @Input() imageTypeMode: ImageTypes = 0;
  @Input() dataObs$: Observable<TypeaheadResults[]>;

  @Output() closeFiltersPaneEmitter = new EventEmitter<any>();

  cancelTypeahead$ = new Subject<void>();

  loading = false;
  configMode = this.getParentConfigureMode;

  truncateText = TruncateString;
  isHandset = false;
  private _subscription;


  trackByUID = TrackByUID;

  selectedHit: { entityName: string; property: string; hitText: string };
  finder: { entityName: string; property: string; hitText: string }[] = [];

  removeMarkupFromString = AppComponent.removeMarkupFromString;

  protected individualSearchSubscription: Subscription;

  protected modularSearchFields: ExtendedModularSearchField<TypeaheadResults, ImageFile>[];
  constructor(private lookupSvc: LookupDataService, 
              private galleryTypeSvc: GalleryTypeService, 
              private cd: ChangeDetectorRef,
              private breakpointService: BreakpointService ) {
    super();
  }

  get fieldsEnabledCount(): number {
    return this.modularSearchFields.filter(f => f.enabled).length;
  }

  ngOnInit() {
    this._subscription = this.breakpointService.handset$.subscribe(
      isHandset => {
        this.isHandset = isHandset;
      }
    );
    this.debounceTimeMs = 500;
    super.ngOnInit();
    this.fieldsChosenChanged();
    this.modularSearchFields = [
      {
        fieldName: 'Keyword',
        displayName: 'Keyword',
        entityProps: ['Keyword', 'ImageKeywords'],
        default: true,
        enabled: true,
        filterClause: ((a: TypeaheadResults, _: string) => a.matches.length && a.matches.filter(k => k.hits['keyword']).length > 0),
        outerFilterClause: (
          (a: ImageFile, searchChars: string) => a.imageKeywords
            && a.imageKeywords.filter(k => k.lU_Keyword.keyword.includes(searchChars)).length > 0)
      },
      {
        fieldName: 'ImageModel',
        displayName: 'Image Name',
        entityProps: ['Name'],
        default: true,
        enabled: true,
        filterClause: ((a: TypeaheadResults, _: string) => a.matches.length && a.matches.filter(k => k.hits['name']).length > 0),
        outerFilterClause: ((a: ImageFile, searchChars: string) => a.name && a.name.length && a.name.includes(searchChars))
      },
      {
        fieldName: 'ImageModel',
        displayName: 'Image Notes',
        entityProps: ['Notes'],
        default: true,
        enabled: true,
        filterClause: ((a: TypeaheadResults, _: string) => a.matches.length && a.matches.filter(k => k.hits['notes']).length > 0),
        outerFilterClause: ((a: ImageFile, searchChars: string) => a.notes && a.notes.length && a.notes.includes(searchChars))
      },
      {
        fieldName: 'Transcription',
        displayName: 'Transcription',
        entityProps: ['Transcription'],
        default: true,
        enabled: true, //this.imageTypeMode === ImageTypes['Letters & Diaries']
          //|| this.imageTypeMode === ImageTypes.Ordnance_Returns
          //|| this.imageTypeMode === 0, // only allowed when viewing letters or ordnance returns (or all)
        filterClause: ((a: TypeaheadResults, _: string) => a.matches.length
          && a.matches.filter(k => k.hits['transcription']).length > 0),
        outerFilterClause: ((a: ImageFile, searchChars: string) => a.transcription
          && a.transcription.length
          && a.transcription.includes(searchChars))
      },
      {
        fieldName: 'ImageModel',
        displayName: 'Call Number',
        entityProps: ['CallNo'],
        default: true,
        enabled: true,
        filterClause: ((a: TypeaheadResults, _: string) => a.matches.length && a.matches.filter(k => k.hits['callNo']).length > 0),
        outerFilterClause: ((a: ImageFile, searchChars: string) => a.callNo && a.callNo.length && a.callNo.includes(searchChars))
      },
      {
        fieldName: 'ImageIndividual',
        displayName: this.imageTypeMode === ImageTypes['Letters & Diaries'] ? 'Author\'s Name' : 'Individual\'s Name',
        entityProps: ['FullName', 'ImageIndividuals'],
        default: true,
        enabled: true,
        filterClause: ((a: TypeaheadResults, _: string) => a.matches.length && a.matches.filter(k => k.hits['imageIndividual']).length > 0),
        outerFilterClause: ((a: ImageFile, searchChars: string) => a
          && a.imageIndividuals
          && a.imageIndividuals.length
          && a.imageIndividuals.filter(b => (b.fullName.length
            && b.fullName.includes(searchChars))).length > 0)
      },
      {
        fieldName: 'ImageIndividual',
        displayName: this.imageTypeMode === ImageTypes['Letters & Diaries'] ? 'Notes about Author' : 'Individual\'s Notes',
        entityProps: ['IndividualNotes'],
        default: true,
        enabled: true,
        filterClause: ((a: TypeaheadResults, _: string) => a.matches.length
          && a.matches.filter(k => k.tableName === 'ImageIndividualsWithFullNameView'
            && k.hits['individualNotes']).length > 0),
        outerFilterClause: ((a: ImageFile, searchChars: string) => a
          && a.imageIndividuals
          && a.imageIndividuals.length
          && a.imageIndividuals.filter(b => (b.notes.length
            && b.notes.includes(searchChars))).length > 0)
      }
    ];

    this.individualSearchSubscription = this.galleryTypeSvc.individualSearchTrigger$.subscribe(
      (searchText) => {
        if (!searchText) {
          this.modularSearchFields.forEach(field => field.enabled = field.default);
          this.clearEvent();
        } else {
          this.modularSearchFields.forEach(field => field.enabled = field.fieldName === 'ImageIndividual');
          this.searchText = searchText;
          this.cancelTypeahead$.next();
          this.loading = false;
          this.executeBroadSearch();
        }
      }
    );
  }
  ngAfterViewInit() {
    super.ngAfterViewInit();
    if (this.galleryTypeSvc.hasReloaded) {
      setTimeout(() => {
        this.updateSearchPostRefresh();
      }, 250);
    }
  }
  ngOnDestroy() {
    this.cancelTypeahead$.next();
    this.cancelTypeahead$.complete();
    super.ngOnDestroy();
  }

  updateSearchPostRefresh() {
    const reloadedSearchParams = this.galleryTypeSvc.getCurrentLibraryFilters();
    if (reloadedSearchParams.searchPhrase) {
      this.modularSearchFields.forEach(field => field.enabled =
        reloadedSearchParams.explicitSearchProperties.some( x => x === field.entityProps[0] ));
      this.searchText = reloadedSearchParams.searchPhrase;
      this.cancelTypeahead$.next();
      this.loading = false;
      this.executeBroadSearch();
    }
  }

  fieldsChosenChanged(z?: IStarbaseModularSearchField<TypeaheadResults>): void {
    if (this.dropDown && this.searchText) {
      this.searchEvent(this.searchText);
    }
  }
  translatePropNameToDisplay(field: string): string {
    let d = this.modularSearchFields.find(a => a.entityProps && a.entityProps.some(b => b.toLowerCase() === field.toLowerCase()));
    if (!d) {
      d = (this.modularSearchFields.find(a => a.fieldName.toLowerCase() === field.toLowerCase()));
    }
    return (d.displayName) || '';
  }

  public configureSearch(): void {
    this.configMode = !this.configMode;
    super.configureSearch();
  }

  none = (a: KeyValue<number, [string]>, b: KeyValue<number, [string]>) => a;
  // mapTypeahead(d: { [key: string]: string[] }): Map<string, string[]> {
  //   return new Map<string, string[]>([

  //   ])
  // }

  public filterOuterResults(data: ImageFile[]) {
    const outerFilters = this.modularSearchFields.filter(f => f.enabled);
    return data.filter(v => outerFilters.some(z => z.outerFilterClause(v, this.searchText)));
  }
  public getFilterExplicitProperties(): string[] {
    return this.modularSearchFields.filter(a => a.enabled && a.entityProps).map(b => b.entityProps.toString()).toString().split(',');
  }

  clearEvent(): void {
    this.cancelTypeahead$.next();
    this.loading = false;
    super.clearEvent();
  }

  executeBroadSearch() {
    if (this.isHandset == true) this.closeFiltersPaneEmitter.emit(false);
    if (!this.dataToShow || !this.dataToShow.length) {
      this.dataToShow[0] = new TypeaheadResults(this.searchText, []);
    } else {
      this.dataToShow[0].userInput = this.searchText;
    }
    this.dataToShow[0].selected = false; // indicate to parent component that no specific suggestion was chosen
    this.keySelectionIndex = -1;
    this.outputModel(this.dataToShow[0]);
  }

  outputSelectedMatch(matchEntityName: string, propertyName: string, hitText: string) {
    this.dataToShow[0].selected = true; // indicate we are choosing a specific term to filter by in the parent component
    this.dataToShow[0].matches = [{ tableName: matchEntityName, hits: { [propertyName]: [hitText] }, matchCount: 1, selected: true }];
    this.outputModel(this.dataToShow[0]);
    this.keySelectionIndex = -1;
  }

  public processKeyEvent(event: KeyboardEvent): void { // override functionality for just Enter key
    switch (event.key) {
      case 'Backspace':
        if (this.searchText.length === 1) {
          this.cancelTypeahead$.next();
          return;
        } else if (this.searchText.length === 0) {
          this.clearEvent();
          return;
        }
        super.processKeyEvent(event);
        break;
      case 'Enter':
        if (this.keySelectionIndex > -1) {
          // const chosen = this.dataToShow[0].matches.find(i => i.selected);
          // this.outputSelectedMatch(chosen.tableName, chosen.hits[0][0], chosen.hits[0][1]);
          this.outputSelectedMatch(this.selectedHit.entityName, this.selectedHit.property, this.selectedHit.hitText);
          return;
        }
        if (this.searchText.length > 1) {
          this.cancelTypeahead$.next();
          this.loading = false;
          this.executeBroadSearch();
        }
        // this.searchEvent(this.searchText);
        break;
      default:
        if (this.loading) {
          return;
        }
        super.processKeyEvent(event);
        break;
    }
  }

  public moveSelectionDown(): void {
    // if there's data and we're not at the end of the list already
    if (this.dataToShow.length
      && this.keySelectionIndex < this.dataToShow[0].totalMatches - 1) {
      this.keySelectionIndex++;
      this.setSelectionIndex(this.keySelectionIndex);
    }
    // console.debug(this.keySelectionIndex);
  }
  public moveSelectionUp(): void {
    // if there's data and we're not at the start of the list already
    if (this.dataToShow.length && this.keySelectionIndex > 0) {
      this.keySelectionIndex--;
      this.setSelectionIndex(this.keySelectionIndex);
    }
  }
  public setTextToDisplay(m: any): string {
    if (!m) {
      return;
    } else if (!m.userInput) {
      return m;
    }
    return m.userInput;
  }

  formattedMatchSubtitleString(m: {[key: string]: string[]}): string {
    if (!m || !m[0]) {
    return;
    }
    return Object.keys(m)[0]; // get property name ?
  }

  protected runSearch(searchChars: string): Observable<TypeaheadResults[]> {
    console.log('Executing library search for ' + searchChars);
    try {
      this.loading = true;
      // return of(this.data.filter(v => filters.some(f => f.filterClause(v, searchChars))));
      const fieldsOn = this.enabledModularFilters();
      this.dataObs$ = this.lookupSvc.typeaheadSearch(
        searchChars,
        fieldsOn.map(g => g.fieldName.replace(' ', '')),
        this.maxResults,
        this.imageTypeMode,
        this.modularSearchFields.filter(f => f.enabled && f.entityProps).map(f => f.entityProps.toString())
      ).pipe(
        switchMap(d => {
          this.data[0] = d;
          if (this.data && this.data.length) {
            this.dataToShow = this.data;
            this.finder = [];
            this.dataToShow[0].matches.map(i => {
              Object.keys(i.hits).forEach(ii => {
                i.hits[ii].forEach(iii => {
                  this.finder.push({
                    entityName: i.tableName,
                    property: ii,
                    hitText: this.removeMarkupFromString(iii)
                  });
                });
              });
            });
          }
          return [this.dataToShow];
        }),
        tap(() => {
          this.keySelectionIndex = -1;
          this.dropDown = true;
        }),
        takeUntil(this.cancelTypeahead$),
        catchError((e) => {
 throw e;
}),
        finalize(() => {
          this.loading = false;
          this.cd.markForCheck();
        })
        );
      return this.dataObs$;
    } catch (e) {
      console.error(e);
      this.errorOutput.emit();
      this.loading = false;
      return of([]);
    }
  }
  private setSelectionIndex(s: number): void {
    if (this.dataToShow.length) {
      this.selectedHit = this.finder[s];
    }
  }

}
